<script>
import cloneDeep from 'lodash/cloneDeep';
import RepCounter from '@components/counters/rep-counter.vue';
import { circuitComputed, fitnessTypeComputed } from '@state/helpers';
import EditableTextArea from '@components/editables/editable-text-area.vue';

export default {
  components: {
    EditableTextArea,
    RepCounter,
  },
  props: {
    circuit: {
      type: Object,
      required: true,
      default: () => {},
    },
    allowEditing: {
      type: Boolean,
      required: true,
      default: false,
    },
  },
  data() {
    return {
      circuitHeroClone: null,
      componentKey: 0,
      editableKey: 0,
      circuitFitnessType: [],
    };
  },
  computed: {
    ...circuitComputed,
    ...fitnessTypeComputed,
  },
  beforeMount() {
    var tempCircuit = cloneDeep(this.circuit);
    if (this.circuit?.fitness_types === undefined) {
      tempCircuit.fitness_types = []
    }
    this.circuitHeroClone = tempCircuit;
  },
  methods: {
    handleUpdateCircuitProps(circuitHeroClone) {
      this.$emit('update-circuit-props', circuitHeroClone);
    },
    updateCircuitTitle(value) {
      this.circuitHeroClone.label = value;
      this.handleUpdateCircuitProps(this.circuitHeroClone);
    },
    onAdjustReps(count) {
      this.circuitHeroClone.circuitReps = count;
      this.handleUpdateCircuitProps(this.circuitHeroClone);
    },
    updateCircuitFitnessType(val) {
      this.circuitHeroClone.fitness_types = val;
      this.handleUpdateCircuitProps(this.circuitHeroClone);
    },
  },
  watch: {
    selectedCircuit(newVal, prevVal) {
      this.circuitHeroClone = cloneDeep(newVal);
      this.componentKey += 1;
    },
    circuit(newVal, prevVal) {
      if (newVal && prevVal && newVal.fitness_types !== prevVal.fitness_types) {
        this.circuitFitnessType = newVal.fitness_types ? newVal.fitness_types : [];
      }
      if (newVal && prevVal && newVal._id !== prevVal._id) {
        this.editableKey += 1;
        this.$el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    },
  },
};
</script>

<template>
  <div class="hero">
    <div class="row title-row">
      <div class="col-7">
        <h6 class="section-title">Circuit Name</h6>
        <h1 class="hero-title">
          <EditableTextArea
            v-if="circuitHeroClone && allowEditing"
            :key="editableKey"
            :text="circuitHeroClone.label"
            color="light"
            :enableAutoExpand="true"
            :placeholderTxt="
              circuitHeroClone._id ? 'Enter Circuit Name' : 'New Circuit'
            "
            mode="edit"
            @update-editable-value="updateCircuitTitle"
          />
          <span v-if="circuitHeroClone && !allowEditing">{{
            circuitHeroClone.label
          }}</span>
        </h1>
      </div>
      <div class="col-2 offset-3 rep-column">
        <RepCounter
          :key="componentKey"
          v-if="circuitHeroClone"
          title="Circuit Sets"
          :light="true"
          :count="circuitHeroClone.circuitReps"
          :allowEditing="allowEditing"
          @adjust-reps="onAdjustReps"
        />
      </div>
    </div>
    <div class="row">
      <div class="col">
        <h6 class="section-title">Circuit Intro</h6>
        <BaseInputText
          v-if="circuitHeroClone && allowEditing"
          class="input-intro"
          v-model="circuitHeroClone.trainerFeedback.intro.text"
          @update="handleUpdateCircuitProps(circuitHeroClone)"
        />
        <div class="intro" v-else>
          {{
            circuitHeroClone &&
            circuitHeroClone.trainerFeedback &&
            circuitHeroClone.trainerFeedback.intro.text
              ? circuitHeroClone.trainerFeedback.intro.text
              : ''
          }}
        </div>
        <div class="helper-text">
          Your client will see this preview text <em>before</em> the circuit.
        </div>
      </div>
      <div class="col">
        <h6 class="section-title">Circuit Outro</h6>
        <BaseInputText
          v-if="circuitHeroClone && allowEditing"
          class="input-intro"
          v-model="circuitHeroClone.trainerFeedback.outro.text"
          @update="handleUpdateCircuitProps(circuitHeroClone)"
        />
        <div class="outro" v-else>
          {{
            circuitHeroClone &&
            circuitHeroClone.trainerFeedback &&
            circuitHeroClone.trainerFeedback.outro.text
              ? circuitHeroClone.trainerFeedback.outro.text
              : ''
          }}
        </div>
        <div class="helper-text">
          Your client will see this preview text <em>when</em> they complete the
          circuit.
        </div>
      </div>
    </div>
    <div class="row">
      <div class="col">
        <h6 class="section-title">Fitness Type</h6>
        <v-select
          multiple
          id="circuitFitnessType"
          name="circuitFitnessType"
          placeholder="Choose a fitness type"
          :options="allFitnessTypes"
          :searchable="false"
          :clearable="false"
          v-model="circuitHeroClone.fitness_types"
          @input="updateCircuitFitnessType"
          :reduce="item => ({ _id: item._id, label: item.label })"
        />
      </div>
      <div class="col"></div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import '@design';

.hero {
  background-color: $color-hero-circuit-bg;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 3.2rem 6rem 4rem;
  width: 100%;

  .intro,
  .outro {
    color: white;
    font-size: 18px;
    font-weight: 600;
    padding-bottom: 1rem;
    padding-top: 0.5rem;
  }

  .title-row {
    margin-bottom: 3.4rem;
  }

  .hero-title {
    color: white;
  }

  .section-title,
  .helper-text {
    color: $color-gray;
  }

  .helper-text {
    font-size: 1.4rem;
  }

  .input-intro {
    // stylelint-disable selector-pseudo-element-no-unknown
    ::v-deep .input {
      margin-bottom: 1rem;

      &:focus {
        background-color: $color-off-white !important;
      }
    }
  }

  ::v-deep .input-textarea {
    line-height: 2.5rem;

    &:focus {
      background-color: $color-off-white !important;
    }
  }

  .rep-column {
    display: flex;
    justify-content: flex-end;
    padding-right: 1.5rem;
    // padding-right: 0;
  }

  .custom-switch.custom-switch-lg .custom-control-label {
      padding-left: 3rem;
      padding-bottom: 2rem;
  }
  .custom-switch.custom-switch-lg .custom-control-label::before {
      height: 2.5rem;
      width: calc(3.5rem + 0.75rem);
      border-radius: 4rem;
  }
  .custom-switch.custom-switch-lg .custom-control-label::after {
      width: calc(2.5rem - 6px);
      height: calc(2.5rem - 6px);
      border-radius: calc(3rem - (2rem / 2));
      box-shadow: 0px 0px 5px #888;
      background: #fff;
  }
  .custom-switch.custom-switch-lg .custom-control-input:checked ~ .custom-control-label::after {
      transform: translateX(calc(2rem - 0.25rem));
  }
  .custom-control-input:checked ~ .custom-control-label::before {
      color: #fff;
      border-color: #3542b9 !important;
      background-color: #3542b9 !important;
  }
  .custom-control-label::before {
    background-color: #ededed !important;
    border-color: #ededed !important;
  }
  .custom-switch .custom-control-label::after {
      top: calc(0.25rem + 3px);
      left: calc(-2.25rem + 3px);
  }
  .custm-switch.switch-active {
      background: #4059E1;
      color: #fff !important;
  }

  .switch-right-icon {
      background: #e5e5e5;
      width: 40px;
      display: flex;
      justify-content: center;
      align-items: center;
      border-radius: 25px 0 0 25px;
  }
  .custm-switch.switch-active .switch-right-icon {
      background: #3542b9;
  }
  .custm-switch {
    background:#f2f2f2;
    color: #92969a;
    border-radius: 5px;
    overflow: hidden;
    height:47px;
  }
}
</style>
